'use client';

import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, useLoadScript, Autocomplete } from '@react-google-maps/api';
import lojas from '@/data/misc/lojas.json';
import Styles from './BensServicosBuscaLojas.css';
import { motion } from 'framer-motion';
import ContainerHome24 from '@/components/atoms/ContainerHome24';
import DropdownCheckbox from '@/components/molecules/DropdownCheckbox/DropdownCheckbox';
import clsx from 'clsx';
import WhiteButton from '@/components/atoms/WhiteButton';
import { isMobile } from '@/utils/browser';
import { unset } from 'lodash';

// Função de distância entre coordenadas (Haversine)
function distanciaKm(coord1, coord2) {
  const R = 6371;
  const dLat = ((coord2.lat - coord1.lat) * Math.PI) / 180;
  const dLng = ((coord2.lng - coord1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((coord1.lat * Math.PI) / 180) *
      Math.cos((coord2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const centerInicial = {
  lat: -23.561684,
  lng: -46.656139,
};

export default function BensServicosBuscaLojas() {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
    libraries: ['places'],
  });

  const [autocomplete, setAutocomplete] = useState(null);
  const [textoBusca, setTextoBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState([]);
  const [buscaAtivada, setBuscaAtivada] = useState(false);
  const [mapCenter, setMapCenter] = useState(centerInicial);
  const [expandido, setExpandido] = useState(true);
  const [lojaSelecionada, setLojaSelecionada] = useState(null);

  const categorias = Array.from(new Set(lojas.map((loja) => loja.categoria)));

  const containerStyle = {
    width: '100%',
    height: isMobile() ? (buscaAtivada ? '913px' : '320px') : buscaAtivada ? '913px' : '232px',
  };

  const processarBusca = async (coordenadas) => {
    setMapCenter(coordenadas);

    const lojasProximas = lojas.filter((loja) => {
      const dist = distanciaKm(coordenadas, loja.localizacao);
      return (
        dist <= 50 &&
        (categoriaSelecionada.length === 0 || categoriaSelecionada.includes(loja.categoria))
      );
    });

    setResultados(lojasProximas);
    setBuscaAtivada(true);

    if (isMobile()) {
      setExpandido(true);
    }
  };

  const handlePlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    const location = place?.geometry?.location;

    if (location) {
      const coordenadas = {
        lat: location.lat(),
        lng: location.lng(),
      };
      processarBusca(coordenadas);
    }
  };

  async function buscarEnderecoManual() {
    if (!textoBusca) return;

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(textoBusca)}&region=br&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}`,
      );
      const data = await res.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const coordenadas = { lat: location.lat, lng: location.lng };
        processarBusca(coordenadas);
      } else {
        alert(`Endereço não encontrado: ${data.status}`);
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao buscar endereço.');
    }
  }

  const handleCategoriaChange = (novaCategorias) => {
    setCategoriaSelecionada(novaCategorias);
  };

  function handleFecharBusca() {
    setTextoBusca('');
    setResultados([]);
    setBuscaAtivada(false);
    setCategoriaSelecionada('');
    setMapCenter(centerInicial);
  }

  async function usarMinhaLocalizacao() {
    if (!navigator.geolocation) {
      alert('Geolocalização não é suportada neste navegador.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coordenadas = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Busca endereço com geocoding reverso
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordenadas.lat},${coordenadas.lng}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}`,
          );

          const data = await res.json();

          if (data.status === 'OK' && data.results.length > 0) {
            const endereco = data.results[0].formatted_address;
            setTextoBusca(endereco);
          } else {
            console.warn('Endereço não encontrado');
            setTextoBusca('');
          }
        } catch (err) {
          console.error('Erro ao converter coordenadas para endereço', err);
          setTextoBusca('');
        }

        processarBusca(coordenadas);
      },
      (error) => {
        console.error(error);
        alert('Não foi possível obter sua localização.');
      },
    );
  }

  useEffect(() => {
    if (!buscaAtivada) return;

    const filtrados = lojas.filter((loja) => {
      const dist = distanciaKm(mapCenter, loja.localizacao);
      return (
        dist <= 50 &&
        (categoriaSelecionada.length === 0 || categoriaSelecionada.includes(loja.categoria))
      );
    });

    setResultados(filtrados);
  }, [categoriaSelecionada, mapCenter, buscaAtivada]);

  useEffect(() => {
    if (buscaAtivada && resultados.length > 0) {
      const resultadosEl = document.getElementById('resultados');
      if (resultadosEl) {
        resultadosEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [buscaAtivada, resultados]);

  const modalHeight = isMobile() ? (expandido ? 480 : 105) : 590;
  const rightScreen = isMobile() ? 'unset' : '0';
  const widthScreen = isMobile() ? '300px' : '416px';
  const leftScreen = isMobile() ? '50%;' : 'unset';
  const isDraggingRef = useRef(false);

  if (!isLoaded) return <p>Carregando mapa...</p>;

  return (
    <>
      <section id="bens-servicos-busca-loja" className={clsx(buscaAtivada ? 'busca-ativada' : '')}>
        {/* Mapa */}
        <div className="mapaContainer">
          <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={12}>
            {resultados.map(
              (loja, index) =>
                loja.localizacao && (
                  <Marker
                    key={index}
                    position={loja.localizacao}
                    title={loja.nome}
                    onClick={() => {
                      setLojaSelecionada(loja);
                      setExpandido(true);
                    }}
                  />
                ),
            )}
          </GoogleMap>

          <div className="fade" />
        </div>

        <ContainerHome24>
          <div className="container">
            {/* Campo de Busca */}
            <div className="busca-titulos">
              <h2 className="busca-titulo">Lojas credenciadas</h2>
              <p className="busca-subtitulo">
                Encontre abaixo nossos parceiros de lojas credenciadas
              </p>
            </div>
            <div className="buscaContainer">
              <div className="campoBuscaWrapper">
                {!buscaAtivada && (
                  <span className="iconeLupa">
                    <img src="assets/bens-e-servicos/ic-search.svg" alt="" />
                  </span>
                )}
                <Autocomplete
                  onLoad={(ref) => setAutocomplete(ref)}
                  onPlaceChanged={handlePlaceChanged}
                >
                  <input
                    type="text"
                    placeholder=""
                    className="campoBusca"
                    value={textoBusca}
                    onChange={(e) => setTextoBusca(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        buscarEnderecoManual();
                      }
                    }}
                  />
                </Autocomplete>

                {buscaAtivada && (
                  <button
                    className="botaoFecharInput"
                    onClick={handleFecharBusca}
                    title="Limpar busca"
                  >
                    <img src="assets/bens-e-servicos/ic-close-busca.svg" alt="" />
                  </button>
                )}

                {textoBusca === '' && !buscaAtivada && (
                  <div className="placeholderFalso">
                    Digite um endereço ou{' '}
                    <button
                      className="botaoLocalizacao"
                      onClick={usarMinhaLocalizacao}
                      type="button"
                    >
                      usar minha localização
                    </button>
                  </div>
                )}
              </div>
              {!buscaAtivada && (
                <button className="botaoBusca" type="button" onClick={buscarEnderecoManual}>
                  Buscar
                </button>
              )}
            </div>

            {/* Resultados com animação */}
            {buscaAtivada && (
              <motion.div
                drag="y"
                dragConstraints={{ top: -modalHeight, bottom: 0 }}
                dragElastic={0.1}
                dragMomentum={false}
                onDragStart={() => {
                  isDraggingRef.current = true;
                }}
                onDragEnd={(event, info) => {
                  isDraggingRef.current = false;
                  if (info.offset.y > 100) {
                    setExpandido(false);
                  } else if (info.offset.y < -100) {
                    setExpandido(true);
                  }
                }}
                animate={{
                  height: expandido
                    ? isMobile()
                      ? '284px'
                      : modalHeight
                    : isMobile()
                      ? '65px'
                      : '60px',
                }}
                initial={{ height: 0 }}
                transition={{ duration: 0.3 }}
                className="resultados"
                id="resultados"
                style={{
                  position: 'absolute',
                  left: leftScreen,
                  height: isMobile() ? '70%' : modalHeight,
                  background: '#fff',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  boxShadow: '0 -2px 10px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  width: isMobile() ? '100%' : widthScreen,
                  right: rightScreen,
                  cursor: 'grab',
                  bottom: isMobile() ? '-593px' : '-682px',
                  maxHeight: '600px;',
                  overflow: 'hidden',
                }}
              >
                <div className="content-title" style={{ cursor: 'pointer' }}>
                  <div className="content-btn">
                    <h2
                      style={{ textAlign: 'center', flex: 1 }}
                      onClick={() => setExpandido((prev) => !prev)}
                    >
                      Resultados
                    </h2>
                    <button
                      className="botaoFecharInput"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandido((prev) => !prev);
                      }}
                      title={expandido ? 'Minimizar' : 'Expandir'}
                    >
                      <img
                        className="icone-modal"
                        src={
                          expandido
                            ? 'assets/bens-e-servicos/ic-close-busca.svg'
                            : 'static/amp/shared/assets/ic_arrow_r.svg'
                        }
                        alt=""
                      />
                    </button>
                  </div>
                  {expandido && (
                    <DropdownCheckbox
                      categorias={categorias}
                      selected={categoriaSelecionada}
                      setSelected={handleCategoriaChange}
                    />
                  )}
                </div>

                {(expandido || isMobile()) && (
                  <div className="content-results" style={{ padding: '16px' }}>
                    {lojaSelecionada ? (
                      <div className="lojaItem" style={{ marginBottom: '16px' }}>
                        <h3>{lojaSelecionada.nome}</h3>
                        <p>Razão social: {lojaSelecionada.razaoSocial}</p>
                        <p>CNPJ: {lojaSelecionada.cnpj}</p>
                        <p>Endereço: {lojaSelecionada.endereco}</p>
                        <button className="ver-todas-btn" onClick={() => setLojaSelecionada(null)}>
                          Ver todas novamente
                        </button>
                      </div>
                    ) : resultados.length === 0 ? (
                      <p>Nenhuma loja encontrada nesse raio.</p>
                    ) : (
                      resultados.map((loja, index) => (
                        <div key={index} className="lojaItem" style={{ marginBottom: '16px' }}>
                          <h3>{loja.nome}</h3>
                          <p>Razão social: {loja.razaoSocial}</p>
                          <p>CNPJ: {loja.cnpj}</p>
                          <p>Endereço: {loja.endereco}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </ContainerHome24>
      </section>
      <style jsx>{Styles}</style>
    </>
  );
}
